import { useMemo, useRef } from 'react';
import type { Options } from 'uplot';
import { ChartCanvas, readCssVar } from './charts/ChartCanvas';
import { ChartColorFallback } from '@/tokens/chart-color-fallbacks';

export type SpectrumMarker = {
  hz: number;
  label: string;
  tone?: 'critical' | 'warn' | 'watch' | 'info';
};

export type SpectrumPanelProps = {
  /** Amplitude samples evenly spaced from 0 Hz to `maxHz`. */
  amplitudes: number[];
  /** Maximum frequency the right edge corresponds to. */
  maxHz: number;
  /** Optional fault-frequency markers drawn as vertical lines + labels. */
  markers?: SpectrumMarker[];
  /** y-axis label, e.g. "g rms". Falls back to "amp". */
  unit?: string;
  state?: 'default' | 'loading';
  /** Use a logarithmic y-axis (useful when peaks dwarf the baseline). */
  logY?: boolean;
  /** LinkedCursor position as a fraction (0..1) of the visible window. */
  cursorFraction?: number | null;
  /**
   * Focus the x-axis on this Hz ± `focusWindowHz` so a fault frequency
   * pops without a manual drag-to-zoom. Null/undefined = full range.
   * Drag-to-zoom on the chart overrides; double-click resets to this
   * focus (or to full range when null).
   */
  focusHz?: number | null;
  focusWindowHz?: number;
  /**
   * Hz to render harmonic guides around. When set, the panel draws
   * faint dashed verticals at 2× / 3× / 4× of this frequency (within
   * the visible x-range). Sidebands at 1× ± a fraction of the
   * fundamental are NOT drawn — those need a known shaft-rate value,
   * which is fault-class-specific. Bearings: BPFI/BPFO/BSF; pass the
   * BPFI/BPFO Hz here and the 2..4× hits jump out. Gear mesh: pass
   * the mesh frequency and 2× / 3× highlight runout vs. fault tone.
   */
  harmonicsOf?: number | null;
  /** How many harmonics to draw (default 4: 2×, 3×, 4×, 5×). */
  harmonicCount?: number;
  /**
   * When true, auto-detect the top peaks in the visible window and
   * label them with their Hz value + amplitude. A peak is a local
   * maximum whose amplitude exceeds `peak * peakThreshold` (default
   * 0.15) and is not within 3 bins of a higher peak (suppresses
   * duplicates from the same lobe). At most `peakCount` peaks are
   * shown (default 5).
   *
   * Useful for "what are the dominant frequencies?" without manual
   * cursor hover. Skips bins whose amplitude is dust to keep noise
   * floors quiet.
   */
  showPeaks?: boolean;
  peakCount?: number;
  peakThreshold?: number;
};

const TONE_VAR: Record<NonNullable<SpectrumMarker['tone']>, string> = {
  critical: '--c-critical',
  warn: '--c-warn',
  watch: '--c-watch',
  info: '--c-info',
};

/**
 * Spectrum (FFT) chart — amplitude over frequency, rendered with
 * uPlot. Comes with:
 *   - drag-to-zoom on the x-axis (frequency band selection)
 *   - double-click to reset zoom
 *   - crosshair with x/y readout under cursor
 *   - vertical markers at known fault frequencies (BPFI/BPFO/etc),
 *     drawn through a uPlot hook so they zoom with the data
 *   - optional log-y axis
 *
 * 3-decimal Hz precision in the tooltip; amplitude rounds to 3 sig
 * figs. Markers persist across zoom.
 */
export function SpectrumPanel({
  amplitudes,
  maxHz,
  markers,
  unit = 'amp',
  state = 'default',
  logY = false,
  cursorFraction = null,
  focusHz = null,
  focusWindowHz = 25,
  harmonicsOf = null,
  harmonicCount = 4,
  showPeaks = false,
  peakCount = 5,
  peakThreshold = 0.15,
}: SpectrumPanelProps) {
  const markersRef = useRef(markers);
  markersRef.current = markers;
  const cursorRef = useRef(cursorFraction);
  cursorRef.current = cursorFraction;
  const harmonicsRef = useRef({ base: harmonicsOf, count: harmonicCount });
  harmonicsRef.current = { base: harmonicsOf, count: harmonicCount };

  // Pre-compute peaks once per amplitude array — drawClear runs on
  // every interaction (cursor moves, scale changes), so peak
  // detection at frame time would burn CPU for no signal.
  const peaks = useMemo(() => {
    if (!showPeaks || amplitudes.length < 3) return [];
    const peak = amplitudes.reduce((a, b) => (b > a ? b : a), 0);
    if (peak === 0) return [];
    const threshold = peak * peakThreshold;
    const candidates: { idx: number; v: number }[] = [];
    for (let i = 1; i < amplitudes.length - 1; i++) {
      const v = amplitudes[i];
      if (v < threshold) continue;
      if (v > amplitudes[i - 1] && v > amplitudes[i + 1]) {
        candidates.push({ idx: i, v });
      }
    }
    // Sort by amplitude desc, then greedy-pick rejecting bins
    // within 3 of an already-picked higher bin (kills duplicate
    // peaks on the same lobe).
    candidates.sort((a, b) => b.v - a.v);
    const picked: { idx: number; v: number }[] = [];
    for (const c of candidates) {
      if (picked.length >= peakCount) break;
      if (picked.some((p) => Math.abs(p.idx - c.idx) <= 3)) continue;
      picked.push(c);
    }
    const step = maxHz / Math.max(1, amplitudes.length - 1);
    return picked.map((p) => ({ hz: p.idx * step, amplitude: p.v }));
  }, [amplitudes, maxHz, showPeaks, peakCount, peakThreshold]);
  const peaksRef = useRef(peaks);
  peaksRef.current = peaks;

  const data = useMemo(() => {
    const n = amplitudes.length;
    const xs = new Float64Array(n);
    if (n > 1) {
      const step = maxHz / (n - 1);
      for (let i = 0; i < n; i++) xs[i] = i * step;
    }
    return [xs, Float64Array.from(amplitudes)] as [Float64Array, Float64Array];
  }, [amplitudes, maxHz]);

  const peak = useMemo(() => {
    let p = 0;
    for (const v of amplitudes) if (v > p) p = v;
    return p;
  }, [amplitudes]);

  const options = useMemo<Omit<Options, 'width' | 'height'>>(() => {
    const stroke = readCssVar('c-info', ChartColorFallback.info);
    const fill = readCssVar('c-info-bg', ChartColorFallback.infoBg);
    const axisColor = readCssVar('c-gray-700', ChartColorFallback.gray700);
    const gridColor = readCssVar('c-gray-300', ChartColorFallback.gray300);

    // x-range rules, in priority order:
    //  1. harmonicsOf set → range fits fundamental + all harmonics
    //     plus 10% headroom, so the operator sees the series together.
    //     focusHz/focusWindowHz lose to this — harmonics-aware view
    //     subsumes the "zoom to one fault" idea.
    //  2. focusHz set → ±focusWindowHz around it.
    //  3. neither → full range.
    let xMin = 0;
    let xMax = maxHz;
    if (harmonicsOf != null && harmonicsOf > 0) {
      xMax = Math.min(maxHz, harmonicsOf * (harmonicCount + 1) * 1.1);
      xMin = Math.max(0, harmonicsOf * 0.5);
    } else if (focusHz != null) {
      xMin = Math.max(0, focusHz - focusWindowHz);
      xMax = Math.min(maxHz, focusHz + focusWindowHz);
    }
    return {
      // uPlot gotcha: `scales.x.range` (whether a static array or a
      // function) is invoked on every commit and *overrides* the bounds
      // the user picked via drag-to-zoom or dblclick reset. To keep
      // those interactions working, we leave the x-scale unconstrained
      // and set the initial visible window once via the `init` hook
      // below — uPlot then treats subsequent setScale calls (drag,
      // dblclick → autoscale) as authoritative.
      scales: {
        x: { time: false },
        y: logY
          ? { distr: 3 as const }
          : {},
      },
      axes: [
        {
          stroke: axisColor,
          grid: { stroke: gridColor, width: 0.5 },
          values: (_u, vals) => vals.map((v) => `${v.toFixed(0)} Hz`),
        },
        {
          stroke: axisColor,
          grid: { stroke: gridColor, width: 0.5 },
          size: 60,
          values: (_u, vals) =>
            vals.map((v) => (v < 1 ? v.toFixed(2) : v.toPrecision(3))),
        },
      ],
      cursor: {
        x: true,
        y: true,
        drag: { x: true, y: false, setScale: true },
      },
      series: [
        { label: 'Hz' },
        {
          label: unit,
          stroke,
          width: 1.2,
          fill,
          spanGaps: true,
          value: (_u, raw) =>
            raw == null ? '—' : raw < 1 ? raw.toFixed(3) : raw.toPrecision(3),
        },
      ],
      hooks: {
        // Initial view — once, on first construction. uPlot owns the
        // scale after this, so drag-to-zoom + dblclick reset (back to
        // data-driven bounds) both work normally. The chart re-mounts
        // via the React `key` on focusHz/harmonicsOf changes, so this
        // hook re-runs whenever the operator picks a new focus.
        init: [
          (u) => {
            u.setScale('x', { min: xMin, max: xMax });
            const yMax = logY
              ? Math.max(2, peak * 1.1)
              : Math.max(0.001, peak * 1.1);
            const yMin = logY ? 1 : 0;
            u.setScale('y', { min: yMin, max: yMax });
          },
        ],
        drawClear: [
          (u) => {
            // Render fault markers + cursor line on every redraw so they
            // survive zoom/pan transitions.
            const ctx = u.ctx;
            const m = markersRef.current;
            if (m && m.length > 0) {
              ctx.save();
              ctx.setLineDash([4, 3]);
              ctx.lineWidth = 1;
              // Marker label font size has no token (smaller than
              // fs-meta, used only on canvas annotations).
              // eslint-disable-next-line pdm/no-raw-tokens
              ctx.font = '10px ui-monospace, monospace';
              for (const mk of m) {
                if (mk.hz < u.scales.x.min! || mk.hz > u.scales.x.max!) continue;
                const x = Math.round(u.valToPos(mk.hz, 'x', true));
                const tone = readCssVar(
                  TONE_VAR[mk.tone ?? 'warn'].replace(/^--/, ''),
                  ChartColorFallback.warn,
                );
                ctx.strokeStyle = tone;
                ctx.fillStyle = tone;
                ctx.beginPath();
                ctx.moveTo(x, u.bbox.top);
                ctx.lineTo(x, u.bbox.top + u.bbox.height);
                ctx.stroke();
                ctx.fillText(mk.label, x + 3, u.bbox.top + 12);
              }
              ctx.restore();
            }
            // Harmonic guides — faint dashed lines at 2..N× the
            // configured base frequency, only the ones inside the
            // current x-range. Uses gray-500 so they read as
            // structure-not-signal next to the BPFI/BPFO markers.
            const harm = harmonicsRef.current;
            if (harm.base != null && harm.base > 0) {
              ctx.save();
              ctx.setLineDash([2, 3]);
              ctx.lineWidth = 1;
              ctx.strokeStyle = readCssVar(
                'c-gray-500',
                ChartColorFallback.gray700,
              );
              ctx.fillStyle = readCssVar(
                'c-gray-700',
                ChartColorFallback.gray700,
              );
              // eslint-disable-next-line pdm/no-raw-tokens
              ctx.font = '9px ui-monospace, monospace';
              for (let n = 2; n <= harm.count + 1; n++) {
                const hz = harm.base * n;
                if (hz < u.scales.x.min! || hz > u.scales.x.max!) continue;
                const x = Math.round(u.valToPos(hz, 'x', true));
                ctx.beginPath();
                ctx.moveTo(x, u.bbox.top + 2);
                ctx.lineTo(x, u.bbox.top + u.bbox.height);
                ctx.stroke();
                ctx.fillText(`${n}×`, x + 2, u.bbox.top + 10);
              }
              ctx.restore();
            }
            // Auto-detected peak labels — drawn after harmonics so
            // peak text overlays the harmonic grid lines, not under.
            const ps = peaksRef.current;
            if (ps && ps.length > 0) {
              ctx.save();
              ctx.fillStyle = readCssVar(
                'c-critical',
                ChartColorFallback.critical,
              );
              ctx.strokeStyle = readCssVar(
                'c-critical',
                ChartColorFallback.critical,
              );
              ctx.setLineDash([1, 2]);
              ctx.lineWidth = 0.8;
              // eslint-disable-next-line pdm/no-raw-tokens
              ctx.font = '10px ui-monospace, monospace';
              const xMin = u.scales.x.min!;
              const xMax = u.scales.x.max!;
              for (const p of ps) {
                if (p.hz < xMin || p.hz > xMax) continue;
                const x = Math.round(u.valToPos(p.hz, 'x', true));
                const y = Math.round(u.valToPos(p.amplitude, 'y', true));
                ctx.beginPath();
                ctx.moveTo(x, y);
                ctx.lineTo(x, u.bbox.top + u.bbox.height);
                ctx.stroke();
                // Collision-aware label placement:
                //   - If a fault marker lives within ±6px of this peak's
                //     x, the marker's "BPFI"-style label already occupies
                //     the space above the chart at y ≈ bbox.top+10.
                //     Shift the peak label DOWN to y+12 so the two
                //     labels don't overlap.
                //   - Otherwise keep the default y-4 (just above peak).
                const collidesWithMarker = (markersRef.current ?? []).some(
                  (mk) => {
                    if (mk.hz < xMin || mk.hz > xMax) return false;
                    return (
                      Math.abs(u.valToPos(mk.hz, 'x', true) - x) <= 6
                    );
                  },
                );
                const labelY = collidesWithMarker ? y + 12 : y - 4;
                ctx.fillText(`${p.hz.toFixed(0)} Hz`, x + 3, labelY);
              }
              ctx.restore();
            }
            // LinkedCursor vertical line — fraction-of-visible-x.
            const cf = cursorRef.current;
            if (cf != null) {
              const f = Math.max(0, Math.min(1, cf));
              const xMin = u.scales.x.min!;
              const xMax = u.scales.x.max!;
              const x = Math.round(
                u.valToPos(xMin + (xMax - xMin) * f, 'x', true),
              );
              ctx.save();
              ctx.setLineDash([4, 2]);
              ctx.lineWidth = 1.4;
              ctx.strokeStyle = readCssVar('c-warn', ChartColorFallback.warn);
              ctx.beginPath();
              ctx.moveTo(x, u.bbox.top);
              ctx.lineTo(x, u.bbox.top + u.bbox.height);
              ctx.stroke();
              ctx.restore();
            }
          },
        ],
      },
    };
  }, [focusHz, focusWindowHz, maxHz, unit, peak, logY, harmonicsOf, harmonicCount]);

  if (state === 'loading' || amplitudes.length === 0) {
    return (
      <div
        role="img"
        aria-label="FFT spectrum loading"
        aria-busy={state === 'loading'}
        className="flex h-40 items-center justify-center rounded-r-card border border-gray-300 bg-gray-50 text-fs-meta text-gray-700"
      >
        {state === 'loading' ? 'Computing FFT…' : 'No spectrum data'}
      </div>
    );
  }

  const a11yLabel = `FFT spectrum 0 to ${maxHz} Hz, peak amplitude ${peak.toFixed(2)} ${unit}${markers?.length ? `, ${markers.length} fault markers` : ''}. Drag to zoom, double-click to reset.`;

  return (
    <figure className="rounded-r-card border border-gray-300 bg-gray-50 p-sp-3">
      <ChartCanvas
        // Key on focusHz so changing focus remounts the chart with the
        // new x-axis range. ChartCanvas otherwise treats options as
        // static across a mount; the alternative is to imperatively
        // setScale on the existing instance, which is more code for
        // the same effect when range changes are user-initiated.
        key={`focus-${focusHz ?? 'none'}-h-${harmonicsOf ?? 'none'}`}
        data={data}
        options={options}
        aspect={3.4}
        ariaLabel={a11yLabel}
      />
      <figcaption className="mt-sp-1 flex justify-between text-fs-eyebrow uppercase tracking-[0.14em] text-gray-700">
        <span>0 Hz</span>
        <span>
          {unit}
          {logY ? ' · log y' : ''} · drag to zoom · dbl-click resets
        </span>
        <span>{maxHz} Hz</span>
      </figcaption>
      {cursorFraction != null ? (
        <div
          aria-hidden
          className="sr-only"
          data-cursor-fraction={cursorFraction}
        />
      ) : null}
    </figure>
  );
}
